import {useEffect, useMemo, useRef, useState, type SetStateAction} from 'react';
import equal from 'fast-deep-equal';
import {executeOrReturnAtomState} from '@netx/core-next/utils/jotai';
import {asyncForEach, uniqueArray} from '@netx/core-next/utils/array';
import {type IAttributeTemplate} from '@netx/core-attribute/schema';
import {useMetadataLookups} from '@netx/core-attribute/hooks/useMetadataLookups';
import {userPrefs} from '@netx/core-user/collections/singletons/userPreferences';
import {useWhenChangedEffect} from '@netx/core-components/hooks/useWhenChangedEffect';
import {getPreference, setPreference} from '@netx/core-user-next/utils';
import {useAttributeSetsProvider} from '@netx/core-attribute-set/contexts/AttributeSetsContext';
import type {AttributeSetId} from '@netx/core-attribute-set/types';
import {useConfirmation} from '@netx/core-components/Confirmation';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {getPrefixedLogger} from '@netx/core-next/instance';

const logger = getPrefixedLogger('useAttributeSetSelection');
const customSet = ['custom'];

const notEmpty = <T extends string[]>(arr: T) =>
	arr.filter((id) => id !== '' && id != null);

export type UseAttributeSetSelectionParams = {
	attributeIds?: number[];
	/** default set ids if no user pref (found) */
	defaultSelection?: AttributeSetId[];
	/** regardless of anything else, these sets will always be selected and at the top */
	enforce?: string[];
	/** hide system attributes from selection */
	hideSystemAttributes?: boolean;
	/** if provided, will load from pref and save to pref (when changed) */
	userPrefKey?: string;
	/** if `true`, confrim changing set selection */
	confirm?: boolean;
	/** callback when selection changes */
	onChange?: (selected: AttributeSetId[]) => void;
};

export const useAttributeSetSelection = ({
	attributeIds,

	confirm,
	defaultSelection,
	hideSystemAttributes,
	enforce,
	onChange,
	userPrefKey,
}: UseAttributeSetSelectionParams) => {
	const attributesSetProvider = useAttributeSetsProvider();
	const confirmation = useConfirmation();

	const setOnceSinceChange = useRef(false);

	const shouldConfirm = useRef(confirm === true);
	const setConfirm = useConstCallback((confirm: boolean) => {
		shouldConfirm.current = confirm;
	});

	const getLoggerPrefix = useConstCallback(
		(str: string) => `${str} (${userPrefKey ? userPrefKey : 'none'}) >`,
	);

	/** attribute templates selected indexed by attribute set id */
	const [attributeSetTemplates, setAttributeSetTemplates] = useState<
		Record<AttributeSetId, IAttributeTemplate[]>
	>({});

	const getSelectedSets = useConstCallback(
		(ignoreDefaultSelection?: boolean) => {
			if (attributeIds) return customSet;

			let selected: string[] = enforce ? [...enforce] : [];
			const _defaultSelection = ignoreDefaultSelection
				? []
				: defaultSelection || [];
			logger.debug(
				getLoggerPrefix('getSelectedSets'),
				'enforce selection >',
				enforce ? enforce.join(', ') : '(none)',
			);
			logger.debug(
				getLoggerPrefix('getSelectedSets'),
				'default selection >',
				_defaultSelection ? `[${_defaultSelection.join(', ')}]` : '(none)',
			);
			if (userPrefKey) {
				logger.debug(
					getLoggerPrefix('getSelectedSets'),
					'getting user pref value',
				);
				const pref = getPreference(userPrefKey);
				if (pref == null) {
					logger.debug(
						getLoggerPrefix('getSelectedSets'),
						`no user pref value`,
					);
					logger.debug(
						getLoggerPrefix('getSelectedSets'),
						'using default selection',
					);
					return _defaultSelection;
				}
				logger.debug(
					getLoggerPrefix('getSelectedSets'),
					'got user pref value >',
					pref,
				);
				const prefIds = pref.toString().trim().replace('<none>', '').split(',');
				logger.debug(
					getLoggerPrefix('getSelectedSets'),
					'parsed user pref value >',
					`[${prefIds.join(', ')}]`,
				);
				selected = [...selected, ...prefIds];
				logger.debug(
					getLoggerPrefix('getSelectedSets'),
					'resulting set ids >',
					`[${selected.join(', ')}]`,
				);
			} else {
				logger.debug(getLoggerPrefix('getSelectedSets'), 'no user pref set');
				selected = [...selected, ..._defaultSelection];
				logger.debug(
					getLoggerPrefix('getSelectedSets'),
					'resulting set ids >',
					`[${selected.join(', ')}]`,
				);
			}
			return [...new Set(notEmpty(selected))];
		},
	);

	const setSelectedPref = useConstCallback((selected: AttributeSetId[]) => {
		logger.debug(
			getLoggerPrefix('setSelectedPref'),
			`[${selected.join(', ')}]`,
		);
		if (!userPrefKey) {
			logger.debug(getLoggerPrefix('setSelectedPref'), 'no user pref key');
			return;
		}
		if (!setOnceSinceChange.current) {
			logger.debug(
				getLoggerPrefix('setSelectedPref'),
				'not updated since last change (ignoring)',
			);
			return;
		}
		const toSet = selected.length ? selected.join(',') : ['<none>'];
		logger.debug(
			getLoggerPrefix('setSelectedPref'),
			'setting user pref value >',
			`[${toSet}]`,
		);
		setPreference(userPrefKey, toSet);
	});

	const [selectedSets, _setSelectedSets] = useState<AttributeSetId[]>(
		attributeIds ? customSet : [],
	);

	const initiallySet = useRef(false);

	const setSelectedSets = useConstCallback(
		async (selected: SetStateAction<AttributeSetId[]>) => {
			const currentSelectedSets = selectedSets;
			const nextSelectedSets = executeOrReturnAtomState(
				selected,
				currentSelectedSets,
			);
			logger.debug(
				getLoggerPrefix('set selectedSetsAtom'),
				'new selection >',
				`[${nextSelectedSets.join(', ')}]`,
			);
			logger.debug(
				getLoggerPrefix('set selectedSetsAtom'),
				'current selection >',
				`[${currentSelectedSets.join(', ')}]`,
			);

			if (equal(nextSelectedSets, currentSelectedSets)) {
				logger.debug(
					getLoggerPrefix('set selectedSetsAtom'),
					'no change (ignoring)',
				);
				setOnceSinceChange.current = true;
				return;
			}

			logger.debug(
				getLoggerPrefix('set selectedSetsAtom'),
				'shouldConfirm >',
				shouldConfirm.current,
				'ignoring because init set has not occurred >',
				!initiallySet.current,
			);
			if (shouldConfirm.current && initiallySet.current) {
				try {
					await confirmation({
						title: i18n.t('i18n.switchAttributeSetsConfirmTitle'),
						message: i18n.t('i18n.switchAttributeSetsConfirmMessage'),
						catchOnCancel: true,
					});
				} catch (e) {
					return;
				}
			}

			logger.debug(
				getLoggerPrefix('set selectedSetsAtom'),
				'updating >',
				`[${nextSelectedSets.join(', ')}]`,
			);

			if (initiallySet.current) {
				onChange?.(nextSelectedSets);
			}
			initiallySet.current = true;

			setSelectedPref(nextSelectedSets);
			_setSelectedSets(nextSelectedSets);

			setOnceSinceChange.current = true;
		},
	);

	// track attribute sets that have been loaded
	const loadedSets = useMemo(() => {
		return attributeIds
			? customSet
			: selectedSets.filter((setId) => attributeSetTemplates[setId] != null);
	}, [attributeSetTemplates, selectedSets, attributeIds]);

	const loadingSets = useMemo(() => {
		return attributeIds
			? []
			: selectedSets.filter(
					(setId) =>
						setId && setId !== '<none>' && attributeSetTemplates[setId] == null,
				);
	}, [attributeSetTemplates, selectedSets, attributeIds]);

	/** all selected attribute template ids */
	const attributeTemplateIds = useMemo(() => {
		return (
			attributeIds ?? [
				...new Set(
					Object.values(attributeSetTemplates)
						.flat()
						.map((attr) => attr.attributeid),
				),
			]
		);
	}, [attributeSetTemplates, attributeIds]);

	/** all selected attribute templates indexed by attribute id */
	const allAttributeTemplates = useMemo(() => {
		return attributesSetProvider.attributesCtx.getByIdsAsObject(
			attributeTemplateIds,
		);
	}, [attributeTemplateIds]);

	const {lookupsAllowed, lookup, attributesWorking} = useMetadataLookups({
		attributeTemplates: attributesSetProvider.attributesCtx.data,
		attributeIds: attributeTemplateIds,
	});

	useEffect(() => {
		logger.debug('userPrefKey update', userPrefKey);
		setOnceSinceChange.current = false;
		if (!userPrefKey) {
			logger.debug('userPrefKey update > no user pref key');
			setSelectedSets(getSelectedSets());
			return;
		}
		let changedOnce = false;
		const unsubs = [
			(() => {
				const listener = (pref: string) => {
					if (pref !== userPrefKey) return;
					if (!changedOnce) {
						logger.debug(
							getLoggerPrefix('userPrefKey update'),
							'ignoring first change',
						);
						changedOnce = true;
						return;
					}
					logger.debug(
						getLoggerPrefix('userPrefKey update'),
						'set selected (ignore default)',
					);
					setSelectedSets(getSelectedSets(true));
				};
				userPrefs.on('change', listener);
				return () => userPrefs.off('change', listener);
			})(),
		];
		setSelectedSets(getSelectedSets());
		return () => {
			unsubs.forEach((unsub) => unsub());
		};
	}, [userPrefKey]);

	useWhenChangedEffect(
		[
			attributeIds,
			selectedSets,
			loadingSets,
			attributesSetProvider.loadingTemplates,
			hideSystemAttributes,
			attributesSetProvider.data,
			attributesSetProvider.allAttributeIds,
		] as const,
		([
			attributeIds,
			selected,
			loading,
			loadingTemplates,
			hideSystemAttributes,
		]) => {
			logger.debug(getLoggerPrefix('changed data'), `[${selected.join(', ')}]`);
			if (loadingTemplates || !attributesSetProvider.allAttributeIds.length) {
				logger.debug(
					getLoggerPrefix('changed data'),
					'waiting for external data...',
				);
				return;
			}

			if (attributeIds) {
				const d = attributeIds.reduce(
					(acc, id) => {
						const template = attributesSetProvider.attributesCtx.getById(id);
						if (template) {
							acc[customSet[0]] = [...(acc[customSet[0]] || []), template];
						}
						return acc;
					},
					{} as Record<AttributeSetId, IAttributeTemplate[]>,
				);
				setAttributeSetTemplates(d);
				return;
			}

			let continueFetching = true;
			const fetch = async () => {
				const allAttributeTemplates: Record<
					AttributeSetId,
					IAttributeTemplate[]
				> = {};

				logger.debug(
					getLoggerPrefix('changed data'),
					'fetch > selected sets >',
					`[${selected.join(', ')}]`,
				);
				logger.debug(
					getLoggerPrefix('changed data'),
					'fetch > loading sets >',
					`[${loading.join(', ')}]`,
				);
				await asyncForEach(selected, async (id) => {
					logger.debug(
						getLoggerPrefix('changed data'),
						'fetching attribute templates for set >',
						id,
					);
					if (!continueFetching) {
						logger.debug(
							getLoggerPrefix('changed data'),
							'fetch > fetch was cancelled',
						);
						return;
					}
					const attributeTemplates =
						await attributesSetProvider.getAttributeTemplatesById(id, {
							omitSystem: hideSystemAttributes,
						});

					logger.debug(
						getLoggerPrefix('changed data'),
						'fetch > fetched attribute templates for set >',
						id,
						`(${attributeTemplates.length})`,
					);
					allAttributeTemplates[id] = attributeTemplates;
				});

				setAttributeSetTemplates((attributeSetTemplates) => {
					const newTemplates = {
						...attributeSetTemplates,
						...allAttributeTemplates,
					};
					Object.keys(newTemplates).forEach((id) => {
						if (!selected.includes(id)) delete newTemplates[id];
					});
					if (equal(newTemplates, attributeSetTemplates))
						return attributeSetTemplates;
					return newTemplates;
				});
			};

			fetch();

			return () => {
				continueFetching = false;
			};
		},
		{
			executeAtMount: true,
		},
	);

	const setOrder = useConstCallback((ids: AttributeSetId[]) => {
		setSelectedSets(uniqueArray(notEmpty(ids)));
	});

	return {
		attributeSetCtx: attributesSetProvider,
		allAttributeTemplates,
		attributeTemplates: attributeSetTemplates,
		attributeTemplateIds,
		attributesWorking,
		enforce,
		loadedSets: attributeIds ? customSet : loadedSets,
		loadingSets,
		loadingTemplates: attributesSetProvider.loadingTemplates,
		lookupsAllowed,
		selected: attributeIds ? customSet : selectedSets,

		lookup,
		setSelected: setSelectedSets,
		setConfirm,
		setOrder,
	};
};
